import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, Info, Eye, EyeOff, KeyRound } from 'lucide-react';
import api from '@/utils/api';
import { setAuth } from '@/utils/auth';
import SEO from '@/components/SEO';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState('');
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Forgot passcode states
  const [showForgotPasscode, setShowForgotPasscode] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (passcode.length < 6) {
      toast.error('Please enter a valid passcode (minimum 6 characters)');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/auth/admin-login', { mobile, passcode });
      
      if (response.data.token) {
        setAuth(response.data.token, response.data.user);
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      } else {
        toast.error('Login failed - no token received');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasscode = async () => {
    if (mobile.length !== 10) {
      toast.error('Please enter your mobile number first');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/auth/admin-forgot-passcode', { mobile });
      if (response.data.verificationId || response.data.message) {
        toast.success('OTP sent to your mobile number');
        setOtpSent(true);
      } else {
        toast.error('Failed to send OTP');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasscode = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    if (newPasscode.length < 6) {
      toast.error('New passcode must be at least 6 characters');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/auth/admin-reset-passcode', { 
        mobile, 
        otp
      });
      
      if (response.data.new_passcode || response.data.message) {
        toast.success(`Passcode reset successfully! Your new passcode is: ${response.data.new_passcode}`);
        setShowForgotPasscode(false);
        setOtpSent(false);
        setOtp('');
        setNewPasscode('');
      } else {
        toast.error('Failed to reset passcode');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset passcode');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      <SEO 
        title="Admin Login | VVVSI Dashboard"
        description="Admin and Executive login portal for VVVSI Job Portal management system."
      />
      
      {/* Custom Header for Admin - Dark Theme */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src="/vvvsi-logo.jpg" alt="VVVSI Logo" className="h-16 md:h-20 w-auto object-contain rounded-lg" />
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>
      
      <main className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-600 mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>Admin Login</h1>
            <p className="text-slate-400">Vallalar Velai Vaaipu Sevai Iyakkam</p>
          </div>

          <Card className="p-8 shadow-2xl border-slate-700 bg-white">
            {!showForgotPasscode ? (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mobile Number</label>
                  <Input
                    data-testid="admin-mobile-input"
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    required
                    maxLength={10}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Passcode</label>
                  <div className="relative">
                    <Input
                      data-testid="admin-passcode-input"
                      type={showPasscode ? "text" : "password"}
                      placeholder="Enter your passcode"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value.slice(0, 20))}
                      required
                      className="h-12 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasscode(!showPasscode)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  data-testid="admin-login-btn"
                  type="submit" 
                  className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white text-lg"
                  disabled={loading || mobile.length !== 10 || passcode.length < 6}
                >
                  {loading ? 'Please wait...' : 'Login'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPasscode(true)}
                    className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                  >
                    Forgot Passcode? (Admin Only)
                  </button>
                </div>

                {/* Info Note */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Admin & Executive Access:</p>
                      <ul className="list-disc list-inside space-y-1 text-amber-700">
                        <li>Enter your registered mobile number</li>
                        <li>Use the passcode provided by admin</li>
                        <li>Account locks after 3 failed attempts</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <KeyRound className="w-12 h-12 text-teal-600 mx-auto mb-2" />
                  <h2 className="text-xl font-semibold text-slate-900">Reset Passcode</h2>
                  <p className="text-sm text-slate-500">Available for main admin only</p>
                </div>

                {!otpSent ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Admin Mobile Number</label>
                      <Input
                        type="tel"
                        placeholder="Enter admin mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        required
                        maxLength={10}
                        className="h-12"
                      />
                    </div>

                    <Button 
                      onClick={handleForgotPasscode}
                      className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white"
                      disabled={loading || mobile.length !== 10}
                    >
                      {loading ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </>
                ) : (
                  <form onSubmit={handleResetPasscode} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Enter OTP</label>
                      <Input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        maxLength={6}
                        className="h-12 text-center text-xl tracking-widest"
                      />
                      <p className="text-xs text-slate-500 mt-1">OTP sent to +91{mobile}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">New Passcode</label>
                      <Input
                        type="text"
                        placeholder="Enter new passcode (min 6 characters)"
                        value={newPasscode}
                        onChange={(e) => setNewPasscode(e.target.value.slice(0, 20))}
                        required
                        className="h-12"
                      />
                    </div>

                    <Button 
                      type="submit"
                      className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white"
                      disabled={loading || otp.length !== 6 || newPasscode.length < 6}
                    >
                      {loading ? 'Resetting...' : 'Reset Passcode'}
                    </Button>
                  </form>
                )}

                <Button 
                  variant="outline"
                  onClick={() => { 
                    setShowForgotPasscode(false); 
                    setOtpSent(false); 
                    setOtp(''); 
                    setNewPasscode(''); 
                  }}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            )}
          </Card>
        </div>
      </main>
      
      {/* Custom Footer for Admin - Dark Theme */}
      <footer className="bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Vallalar Velai Vaaipu Sevai Iyakkam. All rights reserved.
            </p>
            <p className="text-slate-600 text-xs mt-2">
              Admin Portal - Authorized Access Only
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminLogin;
